import React, { useState } from 'react'
import { BBDTag, Horizontal, Vertical, MaterialIcon, GraviButton, Texto } from '@gravitate-js/excalibrr'
import { Popover, Select, message } from 'antd'
import { useSupplierReconciliation } from '../../../../../api/useSupplierReconciliation'
import { useMasterMetadata } from '../../../../../api/useMetadata/useMasterMetadata'
import type { SupplierConfigMetadataResponse } from '../../../../../api/useMetadata/types'

interface UnmappedTagProps {
  type: 'product' | 'terminal'
  sourceName: string
  supplierId?: string
  supplierName?: string
  onMapped?: () => void
}

export function UnmappedTag({ type, sourceName, supplierId, supplierName, onMapped }: UnmappedTagProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState<string | undefined>()
  const [isLoading, setIsLoading] = useState(false)

  // Get metadata for products/terminals
  const { data: metadata } = useMasterMetadata<SupplierConfigMetadataResponse>('supplier_config')

  // Get mapping mutations
  const { useUpsertProductMapping, useUpsertTerminalMapping } = useSupplierReconciliation()
  const productMappingMutation = useUpsertProductMapping()
  const terminalMappingMutation = useUpsertTerminalMapping()

  // Get options based on type
  const options = React.useMemo(() => {
    if (type === 'product' && metadata?.product_hierarchy) {
      return Object.values(metadata.product_hierarchy)
        .flat()
        .map((product) => ({
          value: product.id,
          label: product.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    } else if (type === 'terminal' && metadata?.locations) {
      return metadata.locations
        .map((terminal) => ({
          value: terminal.id,
          label: terminal.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }
    return []
  }, [type, metadata])

  const handleMapping = async () => {
    if (!selectedValue || !supplierId) {
      message.error(`Please select a ${type} and ensure supplier is set`)
      return
    }

    setIsLoading(true)

    try {
      if (type === 'product') {
        await productMappingMutation.mutateAsync({
          body: {
            supplier_id: supplierId,
            source_name: sourceName,
            product_ids: [selectedValue],
          },
        })
      } else {
        await terminalMappingMutation.mutateAsync({
          body: {
            supplier_id: supplierId,
            source_name: sourceName,
            terminal_ids: [selectedValue],
          },
        })
      }

      message.success(`${type === 'product' ? 'Product' : 'Terminal'} mapped successfully`)
      setIsPopoverOpen(false)
      onMapped?.()
    } catch (error) {
      message.error(`Failed to map ${type}`)
    } finally {
      setIsLoading(false)
    }
  }

  const popoverContent = (
    <Vertical gap={12}>
      <Texto category='heading-small'>Map {type === 'product' ? 'Product' : 'Terminal'}</Texto>
      <Texto category='p2' appearance='hint'>
        Map "{sourceName}" to a {type} for {supplierName || 'this supplier'}
      </Texto>

      <Select
        placeholder={`Select ${type}`}
        options={options}
        value={selectedValue}
        onChange={setSelectedValue}
        showSearch
        filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        style={{ width: '100%' }}
      />

      <Horizontal gap={8} justifyContent='flex-end'>
        <GraviButton
          size='small'
          appearance='outlined'
          onClick={() => {
            setIsPopoverOpen(false)
            setSelectedValue(undefined)
          }}
        >
          Cancel
        </GraviButton>
        <GraviButton size='small' theme2 onClick={handleMapping} loading={isLoading} disabled={!selectedValue}>
          Map
        </GraviButton>
      </Horizontal>
    </Vertical>
  )

  return (
    <Popover content={popoverContent} trigger='click' open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <Horizontal
        alignItems='stretch'
        style={{
          cursor: 'pointer',
          overflow: 'visible',
          display: 'inline-flex',
          width: 'min-content',
          whiteSpace: 'nowrap',
        }}
      >
        <Vertical
          verticalCenter
          borderRadius='6px 0 0 6px'
          style={{
            minWidth: 20,
            background: 'var(--theme-error)',
            height: '22px',
            border: 'solid 1px var(--theme-error)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px',
          }}
        >
          <MaterialIcon
            icon='add'
            size={14}
            style={{
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        </Vertical>

        <Vertical
          verticalCenter
          style={{
            fontSize: '11px',
            fontWeight: 600,
            background: 'var(--bg-2)',
            borderRadius: '0 6px 6px 0',
            height: '22px',
            border: 'solid 1px var(--gray-300)',
            borderLeft: 'none',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '8px',
            paddingRight: '8px',
          }}
        >
          <Texto category='label' style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
            {sourceName}
          </Texto>
        </Vertical>
      </Horizontal>
    </Popover>
  )
}
